/**
 * ─── Philippine Tax Computation Service ──────────────────────
 * BIR Form 2316 withholding tax computation per TRAIN Law.
 * Graduated income tax rates for compensation income earners.
 *
 * Metadata-driven: tax brackets, exemption thresholds, and
 * contribution rates are resolved from tenant PayrollConfig.
 *
 * References:
 *  - RA 10963 (TRAIN Law) — graduated withholding tax table
 *  - BIR Revenue Regulations No. 11-2018
 *  - Annual tax table for purely compensation income earners
 */

import type { TaxStatus, Form2316, Employee, PayrollConfig, Payslip } from './hris.schema'
import { DEFAULT_PAYROLL_CONFIG, TAX_EXEMPTION_THRESHOLD } from './hris.schema'

// ─── Withholding Tax Brackets (TRAIN Law — Annual) ───────────

interface TaxBracket {
  /** Upper bound of bracket (exclusive). Infinity for last bracket. */
  upperBound: number
  /** Fixed tax amount for this bracket */
  fixedTax: number
  /** Tax rate on excess over lower bound */
  rateOnExcess: number
}

/**
 * Graduated tax brackets for annual taxable income (TRAIN Law).
 * Effective January 1, 2023 onwards (as amended by RA 10963).
 */
const ANNUAL_TAX_BRACKETS: TaxBracket[] = [
  { upperBound: 250000, fixedTax: 0, rateOnExcess: 0 },
  { upperBound: 400000, fixedTax: 0, rateOnExcess: 0.20 },
  { upperBound: 800000, fixedTax: 30000, rateOnExcess: 0.225 },
  { upperBound: 2000000, fixedTax: 102500, rateOnExcess: 0.25 },
  { upperBound: 8000000, fixedTax: 502500, rateOnExcess: 0.30 },
  { upperBound: Infinity, fixedTax: 2302500, rateOnExcess: 0.35 },
]

// ─── Withholding Tax Table for Monthly Compensation ──────────



// ═══════════════════════════════════════════════════════════════
// Tax Service
// ═══════════════════════════════════════════════════════════════

export class TaxService {
  private config: PayrollConfig

  constructor(config: PayrollConfig = DEFAULT_PAYROLL_CONFIG) {
    this.config = config
  }

  /**
   * Compute annual tax due using graduated rates (TRAIN Law).
   *
   * @param annualTaxableIncome — Total taxable compensation for the year
   * @returns Total tax due for the year
   */
  static computeAnnualTax(annualTaxableIncome: number): number {
    if (annualTaxableIncome <= 0) return 0

    // Find applicable bracket
    let lowerBound = 0
    for (const bracket of ANNUAL_TAX_BRACKETS) {
      if (annualTaxableIncome <= bracket.upperBound) {
        const excess = annualTaxableIncome - lowerBound
        return Math.round((bracket.fixedTax + excess * bracket.rateOnExcess) * 100) / 100
      }
      lowerBound = bracket.upperBound
    }

    // Fallback (shouldn't reach — last bracket is Infinity)
    return 0
  }

  /**
   * Compute monthly withholding tax using the prescribed withholding
   * tax table method. This is what employers use for payroll deduction.
   *
   * @param monthlyTaxableIncome — Taxable compensation for the period
   * @param payPeriodsPerYear — 12 (monthly) or 24 (semi-monthly)
   */
  static computeMonthlyWithholdingTax(
    monthlyTaxableIncome: number,
    payPeriodsPerYear: number = 12,
  ): number {
    if (monthlyTaxableIncome <= 0) return 0

    const annualTaxableIncome = monthlyTaxableIncome * payPeriodsPerYear
    const annualTax = TaxService.computeAnnualTax(annualTaxableIncome)
    
    return Math.round((annualTax / payPeriodsPerYear) * 100) / 100
  }

  /**
   * Compute tax-exempt 13th month pay and other benefits.
   * Per TRAIN law: up to 90,000 PHP of 13th month pay + other benefits
   * is tax-exempt. Any excess is taxable.
   */
  static computeNonTaxableBenefits(
    thirteenthMonthPay: number,
    otherBonuses: number,
    deMinimisBenefits: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): {
    nonTaxableThirteenthMonth: number
    taxableThirteenthMonth: number
    nonTaxableDeMinimis: number
    taxableDeMinimis: number
  } {
    const totalOtherBenefits = thirteenthMonthPay + otherBonuses
    const exemptCap = config.thirteenthMonthExemptCap

    // 13th month + other bonuses exempt up to cap
    const nonTaxableThirteenthMonth = Math.min(totalOtherBenefits, exemptCap)
    const taxableThirteenthMonth = Math.max(0, totalOtherBenefits - exemptCap)

    // De minimis benefits are fully exempt up to their individual caps
    // We assume the deMinimisBenefits passed in is already within caps
    const nonTaxableDeMinimis = deMinimisBenefits
    const taxableDeMinimis = 0 // De minimis beyond caps should be tracked separately

    return {
      nonTaxableThirteenthMonth,
      taxableThirteenthMonth,
      nonTaxableDeMinimis,
      taxableDeMinimis,
    }
  }

  /**
   * Compute the total annual tax due and populate Form 2316 fields.
   */
  static computeForm2316(
    employee: Employee,
    payslips: Payslip[],
    taxYear: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
    taxWithheldPreviousEmployer?: number,
  ): Omit<Form2316, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'version' | 'createdBy' | 'updatedBy'> {
    // Sum all compensation from payslips
    const totalBasicPay = payslips.reduce((s, p) => s + p.basicPay, 0)
    const totalOvertimePay = payslips.reduce((s, p) => s + p.overtimePay, 0)
    const totalHolidayPay = payslips.reduce((s, p) => s + p.holidayPay, 0)
    const totalNightDiff = payslips.reduce((s, p) => s + p.nightDifferential, 0)
    const totalAllowance = payslips.reduce((s, p) => s + p.allowance, 0)
    const totalOtherEarnings = payslips.reduce((s, p) => s + p.otherEarnings, 0)
    const totalThirteenthMonth = payslips.reduce((s, p) => s + p.thirteenthMonthPay, 0)

    // De minimis (from config — these are per-period caps)
    // For annual: multiply per-period caps by number of periods
    const annualDeMinimis = (
      config.deMinimisRiceSubsidy +
      config.deMinimisUniformAllowance +
      config.deMinimisMedicalAllowance +
      config.deMinimisLaundryAllowance
    ) * payslips.length

    // Gross compensation
    const grossCompensation = totalBasicPay + totalOvertimePay + totalHolidayPay +
      totalNightDiff + totalAllowance + totalOtherEarnings
    const totalCompensation = grossCompensation + totalThirteenthMonth + annualDeMinimis

    // Non-taxable benefits
    const { nonTaxableThirteenthMonth, nonTaxableDeMinimis } =
      TaxService.computeNonTaxableBenefits(totalThirteenthMonth, 0, annualDeMinimis, config)

    // Government contributions (employee share only — these are excluded from taxable income)
    const totalSSS = payslips.reduce((s, p) => s + p.sssContribution, 0)
    const totalPhilHealth = payslips.reduce((s, p) => s + p.philhealthContribution, 0)
    const totalPagIBIG = payslips.reduce((s, p) => s + p.pagibigContribution, 0)

    const totalExempt = nonTaxableThirteenthMonth + nonTaxableDeMinimis +
      totalSSS + totalPhilHealth + totalPagIBIG

    // Taxable income
    const taxableIncome = Math.max(0, totalCompensation - totalExempt)

    // Tax due using graduated rates
    const taxDue = TaxService.computeAnnualTax(taxableIncome)

    // Compare with total withholding tax already paid
    const totalTaxWithheld = payslips.reduce((s, p) => s + p.withholdingTax, 0)
    const totalTaxPaid = totalTaxWithheld + (taxWithheldPreviousEmployer ?? 0)

    const taxOverWithheld = Math.max(0, totalTaxPaid - taxDue)
    const taxUnderWithheld = Math.max(0, taxDue - totalTaxPaid)

    return {
      tenantId: employee.tenantId,
      employeeId: employee.id,
      employeeName: employee.fullName,
      employeeTIN: employee.tin,
      taxYear,
      employeeAddress: employee.address,
      employeeBirthDate: employee.dateOfBirth,
      employeeCivilStatus: employee.civilStatus,
      employeeTaxStatus: employee.taxStatus,
      employerName: config.companyName,
      employerTIN: config.companyTIN,
      employerAddress: config.companyAddress,
      employerZipCode: config.companyZipCode,
      grossCompensation,
      thirteenthMonthPay: totalThirteenthMonth,
      otherBonuses: 0,
      deMinimisBenefits: annualDeMinimis,
      totalCompensation,
      nonTaxableThirteenthMonth,
      nonTaxableDeMinimis,
      sssGsisContributions: totalSSS,
      philhealthContributions: totalPhilHealth,
      pagibigContributions: totalPagIBIG,
      totalExempt,
      taxableIncome,
      taxDue,
      taxWithheldPreviousEmployer,
      totalTaxWithheld: totalTaxPaid,
      taxOverWithheld,
      taxUnderWithheld,
      preparedBy: 'system',
      preparedDate: new Date().toISOString().slice(0, 10),
      status: 'draft',
    }
  }

  /**
   * Format Form 2316 data as printable markdown (for PDF generation).
   */
  static formatForm2316AsText(form: Omit<Form2316, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'version' | 'createdBy' | 'updatedBy'>): string {
    const nf = (n: number) => `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`
    
    return `
═══════════════════════════════════════════════════════════════
  BIR FORM 2316 — Certificate of Compensation Payment
                   Tax Year ${form.taxYear}
═══════════════════════════════════════════════════════════════

PART I — EMPLOYEE INFORMATION
  Name:           ${form.employeeName}
  TIN:            ${form.employeeTIN}
  Address:        ${form.employeeAddress}
  Birth Date:     ${form.employeeBirthDate}
  Civil Status:   ${form.employeeCivilStatus}
  Tax Status:     ${form.employeeTaxStatus}

PART II — EMPLOYER INFORMATION
  Employer:       ${form.employerName}
  Employer TIN:   ${form.employerTIN}
  Address:        ${form.employerAddress}

PART III — COMPENSATION INCOME
  Gross Compensation:          ${nf(form.grossCompensation)}
  13th Month Pay:              ${nf(form.thirteenthMonthPay)}
  Other Bonuses:               ${nf(form.otherBonuses)}
  De Minimis Benefits:         ${nf(form.deMinimisBenefits)}
  ─────────────────────────────────────
  Total Compensation:          ${nf(form.totalCompensation)}

PART IV — NON-TAXABLE / EXEMPT
  Non-Taxable 13th Month:      ${nf(form.nonTaxableThirteenthMonth)}
  Non-Taxable De Minimis:      ${nf(form.nonTaxableDeMinimis)}
  SSS/GSIS Contributions:      ${nf(form.sssGsisContributions)}
  PhilHealth Contributions:    ${nf(form.philhealthContributions)}
  Pag-IBIG Contributions:      ${nf(form.pagibigContributions)}
  ─────────────────────────────────────
  Total Exempt:                ${nf(form.totalExempt)}

PART V — TAXABLE INCOME
  Taxable Income:              ${nf(form.taxableIncome)}

PART VI — TAX DUE
  Tax Due (Graduated Rates):   ${nf(form.taxDue)}
  Tax Withheld (This Employer):${nf(form.totalTaxWithheld)}
  Tax Over-Withheld (Refund):  ${nf(form.taxOverWithheld)}
  Tax Under-Withheld (Pay):    ${nf(form.taxUnderWithheld)}

═══════════════════════════════════════════════════════════════
  Prepared by: ${form.preparedBy} on ${form.preparedDate}
  Status: ${form.status}
═══════════════════════════════════════════════════════════════
`.trim()
  }
}
